import { initHistory, updateScores } from './RaceTracker';
import { getRule } from './RuleLogic';

export const GAMEPLAY_SESSION_STORAGE_KEY = 'gameplaySessionId';
const LEGACY_GAMEPLAY_STORAGE_KEYS = [
    'grandPrixData',
    'currentPlayers',
    'mapCounter',
    'scoreHistory',
    'currentGrandPrix',
];

export function clearLegacyGameplayStorage() {
    LEGACY_GAMEPLAY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

export function normalizeDraftPlayer(player) {
    const playerId = String(player.playerId || '');
    const characterId = String(player.characterId || '0');
    return {
        playerId,
        characterId,
        fullName: player.fullName ||
            `${player.firstName || ''} ${player.lastName || ''}`.trim() || `Player ${playerId}`,
        ...(player.elo === undefined ? {} : { elo: Number(player.elo) }),
    };
}

export function createGameplayDraft({
    title,
    ruleInfo,
    hardcore,
    engineClassId,
    roster,
    savedRoster = roster,
}) {
    const normalizedRoster = roster.map(normalizeDraftPlayer);
    const normalizedSavedRoster = savedRoster.map(normalizeDraftPlayer);
    return {
        schemaVersion: 1,
        setup: {
            title,
            ruleId: String(ruleInfo.ruleId),
            hardcore,
            engineClassId: String(engineClassId),
            raceCount: Number(ruleInfo.raceCount),
            groupSize: Number(ruleInfo.groupSize),
            roster: normalizedRoster,
            savedRoster: normalizedSavedRoster,
        },
        races: [],
        courseIndex: 0,
        currentPlayers: normalizedRoster.slice(0, Number(ruleInfo.groupSize)),
        tieBreak: { active: false, playerIds: [] },
    };
}

export function isGameplayDraftV1(value) {
    return value?.schemaVersion === 1 && value?.setup && Array.isArray(value.races) &&
        Array.isArray(value.currentPlayers) && Array.isArray(value.setup.roster);
}

export function getAuthoritativeRaceCount(draft) {
    const rosterSize = draft?.setup?.roster?.length;
    const groupSize = Number(draft?.setup?.groupSize);
    const rule = getRule(rosterSize, groupSize);

    if (!rule || String(rule.ruleId) !== String(draft?.setup?.ruleId)) {
        throw new Error('The Grand Prix rule does not match its roster and group size.');
    }
    return Number(rule.raceCount);
}

export function shouldEvaluateTieBreak(draft) {
    return Boolean(draft?.tieBreak?.active) ||
        Number(draft?.courseIndex) >= getAuthoritativeRaceCount(draft);
}

export function deriveScoreHistory(draft) {
    let history = initHistory(draft.setup.roster);
    draft.races.forEach((race) => {
        history = updateScores(
            history,
            race,
            draft.setup.ruleId,
            draft.setup.savedRoster || draft.setup.roster,
        );
    });
    return history;
}

export function applyCharactersToRoster(roster, racePlayers) {
    const characters = new Map(racePlayers.map((player) => [
        String(player.playerId),
        String(player.characterId),
    ]));
    return roster.map((player) => characters.has(String(player.playerId)) ? {
        ...player,
        characterId: characters.get(String(player.playerId)),
    } : player);
}

export function isCompleteHeat(currentPlayers, racePlayers) {
    if (currentPlayers.length !== racePlayers.length) return false;
    const expected = new Set(currentPlayers.map((player) => String(player.playerId)));
    return racePlayers.every((player) => expected.has(String(player.playerId))) &&
        new Set(racePlayers.map((player) => String(player.playerId))).size === expected.size;
}

export function resolveLineupPlayer(roster, playerId) {
    const selected = roster.find(
        (player) => String(player.playerId) === String(playerId),
    );
    return selected ? normalizeDraftPlayer(selected) : null;
}

export function resolveLineupPlayers(roster, playerIds) {
    return playerIds
        .map((playerId) => resolveLineupPlayer(roster, playerId))
        .filter(Boolean);
}
